import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  daysOverdue,
  formatCents,
  formatDate,
  formatDateTime,
  parseCents,
  PAYMENT_METHOD_LABELS,
  type DiscountType,
  type LineItemInput,
  type PaymentMethod,
} from '@fsm/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { DeleteRecord } from '../components/Selection';
import { keys } from '../lib/queries';
import { LineItemEditor } from '../components/LineItemEditor';
import { PaymentBadge } from './Invoices';
import { PaymentRequestPanel } from '../components/PaymentRequests';
import { CommunicationLog } from '../components/CommunicationLog';
import { DocumentActions } from '../components/DocumentActions';
import { ContactCard } from '../components/ContactCard';
import { Icon } from '../components/icons';
import { PageFooter, useCrumb } from '../components/Layout';
import {
  ActivityFeed,
  FLOW_ICONS,
  FlowBar,
  flowIcon,
  RecordHeader,
  shortDate,
  type FeedEntry,
  type FlowStep,
} from '../components/record';
import {
  Badge,
  Button,
  Callout,
  Card,
  Dot,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  SummaryBand,
  Textarea,
  Toast,
  cx,
} from '../components/ui';

/**
 * The invoice page.
 *
 * The same three columns as the job and the estimate. The middle runs money
 * first: an invoice exists to be settled, so what has come in sits above what
 * was charged — the balance is the number anyone opening this page is looking
 * for, and it should not be below a table they have to scroll past.
 *
 * No total, tax or balance is worked out here. `invoice.totals` is the
 * server's, and the line-item editor renders the same numbers the print and
 * email views do.
 */
export function InvoiceDetailPage() {
  const { can } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<LineItemInput[]>([]);
  const [discount, setDiscount] = useState<{
    type: DiscountType;
    valueCents: number;
    valueBps: number;
  }>({ type: 'none', valueCents: 0, valueBps: 0 });
  const [taking, setTaking] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: 'error' | 'success' } | null>(null);
  const [emailNote, setEmailNote] = useState<string | null>(null);

  const { data: invoice, isLoading } = useQuery({
    queryKey: keys.invoice(id ?? ''),
    queryFn: () => api.invoices.get(id!),
    enabled: Boolean(id),
  });

  useCrumb(invoice ? `Invoice #${invoice.number}` : null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.invoice(id!) });
    void qc.invalidateQueries({ queryKey: ['invoices'] });
    void qc.invalidateQueries({ queryKey: ['reports'] });
    void qc.invalidateQueries({ queryKey: ['jobs'] });
  };

  const fail = (e: unknown, fallback: string) =>
    setToast({ message: (e as { message?: string }).message ?? fallback, tone: 'error' });

  const save = useMutation({
    mutationFn: () =>
      api.invoices.update(id!, {
        lineItems: draft,
        discountType: discount.type,
        discountValueCents: discount.valueCents,
        discountValueBps: discount.valueBps,
      }),
    onSuccess: () => {
      setEditing(false);
      refresh();
    },
    onError: (e) => fail(e, 'Could not save'),
  });

  const sendEmail = useMutation({
    mutationFn: () => api.invoices.email(id!),
    onSuccess: (result) => {
      setEmailNote(
        result.ok
          ? `Sent to ${result.to}${result.pdfAttached ? ' with the PDF attached' : ' — the PDF could not be generated, so the email carries the link only'}`
          : `Could not send: ${result.failureReason ?? 'unknown error'}`,
      );
      refresh();
    },
    onError: (error: Error) => setEmailNote(`Could not send: ${error.message}`),
  });

  const send = useMutation({
    mutationFn: () => api.invoices.send(id!),
    onSuccess: () => {
      refresh();
      setToast({ message: 'Invoice marked as sent', tone: 'success' });
    },
    onError: (e) => fail(e, 'Could not mark it sent'),
  });

  const voidPayment = useMutation({
    mutationFn: (paymentId: string) => api.invoices.voidPayment(id!, paymentId),
    onSuccess: refresh,
    onError: (e) => fail(e, 'Could not void that payment'),
  });

  if (isLoading || !invoice) {
    return (
      <div className="grid flex-1 place-items-center">
        <Spinner className="size-6 text-ink-4" />
      </div>
    );
  }

  const totals = invoice.totals;
  const balance = totals?.balanceDueCents ?? 0;
  const overdue = balance > 0 ? daysOverdue(invoice.dueAt) : 0;
  const isVoid = invoice.status === 'void';
  const locked = isVoid || (totals?.amountPaidCents ?? 0) > 0;
  const payments = invoice.payments ?? [];
  const hasEmail = Boolean(invoice.customer?.email);

  function startEditing() {
    setDraft(
      (invoice!.lineItems ?? []).map((li) => ({
        priceBookItemId: li.priceBookItemId,
        name: li.name,
        description: li.description,
        kind: li.kind,
        quantity: li.quantity,
        unitPriceCents: li.unitPriceCents,
        taxable: li.taxable,
        position: li.position,
      })),
    );
    setDiscount({
      type: invoice!.discountType,
      valueCents: invoice!.discountValueCents,
      valueBps: invoice!.discountValueBps,
    });
    setEditing(true);
  }

  /*
   * Sent, then paid. An invoice has exactly two things that can happen to it,
   * and a void one has had the second taken away — so that stage goes red
   * rather than the bar pretending it is still waiting for money.
   */
  const flowSteps: FlowStep[] = [
    {
      key: 'sent',
      label: 'Sent',
      icon: flowIcon(FLOW_ICONS.send),
      done: invoice.status !== 'draft',
      sub: invoice.issuedAt
        ? shortDate(invoice.issuedAt)
        : hasEmail
          ? 'Email it'
          : 'No address',
      onClick:
        invoice.status === 'draft'
          ? hasEmail
            ? () => sendEmail.mutate()
            : () => send.mutate()
          : null,
    },
    {
      key: 'paid',
      label: isVoid ? 'Void' : 'Paid',
      icon: flowIcon(FLOW_ICONS.pay),
      done: Boolean(totals?.isSettled) && !isVoid,
      failed: isVoid,
      sub: isVoid
        ? 'Cancelled'
        : totals?.isSettled
          ? 'Settled'
          : `${formatCents(balance)} due`,
      onClick: !isVoid && balance > 0 ? () => setTaking(true) : null,
    },
  ];

  const feedEntries: FeedEntry[] = [
    { id: 'created', at: invoice.createdAt, tone: 'status', title: 'Invoice raised', meta: '' },
    ...(invoice.issuedAt
      ? [
          {
            id: 'issued',
            at: invoice.issuedAt,
            tone: 'status' as const,
            title: 'Sent to the customer',
            meta: invoice.customer?.email ?? '',
          },
        ]
      : []),
    ...payments.map((p) => ({
      id: `p:${p.id}`,
      at: p.createdAt,
      tone: (p.voidedAt ? 'muted' : 'money') as 'muted' | 'money',
      title: p.voidedAt
        ? `Voided: ${formatCents(p.amountCents)} (${PAYMENT_METHOD_LABELS[p.method].toLowerCase()})`
        : `Payment of ${formatCents(p.amountCents)} (${PAYMENT_METHOD_LABELS[p.method].toLowerCase()})`,
      meta: p.receivedByName ?? 'Office',
      detail: p.reference,
    })),
  ];

  return (
    <>
      <RecordHeader
        eyebrow="Invoice"
        number={`#${invoice.number}`}
        title="Invoice"
        titleTail={`#${invoice.number}`}
        badges={
          <>
            <PaymentBadge invoice={invoice} />
            {overdue > 0 && <Badge tone="red">{overdue} days overdue</Badge>}
          </>
        }
        subtitle={
          <>
            <Icon name="users" size={14} className="text-ink-4" />
            <Link to={`/customers/${invoice.customerId}`} className="hover:text-accent">
              {invoice.customer?.name}
            </Link>
            {invoice.job && (
              <>
                <Dot />
                <Link
                  to={`/jobs/${invoice.job.id}`}
                  className="inline-flex items-center gap-1 font-medium text-accent hover:underline"
                >
                  Job #{invoice.job.number}
                  <Icon name="arrow" size={12} />
                </Link>
              </>
            )}
          </>
        }
        /*
         * Sending and taking money are stages, so they live on the flow bar,
         * and editing lives on the card it edits. What is left up here is the
         * document itself — seeing it, saving it, sending it again.
         */
        actions={
          editing ? (
            <>
              <Button onClick={() => setEditing(false)}>Cancel</Button>
              <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
                Save changes
              </Button>
            </>
          ) : (
            <>
              <DocumentActions
                doc={{
                  label: `Invoice #${invoice.number}`,
                  filename: `invoice-${invoice.number}.pdf`,
                  html: () => api.invoices.printHtml(invoice.id),
                  pdf: () => api.invoices.pdf(invoice.id),
                }}
                onError={(message) => setToast({ message, tone: 'error' })}
              />
              <Button
                variant="primary"
                icon="mail"
                loading={sendEmail.isPending}
                disabled={!hasEmail || invoice.status === 'draft'}
                title={
                  hasEmail
                    ? invoice.status === 'draft'
                      ? 'Send it from the flow bar first'
                      : `Email it to ${invoice.customer?.email}`
                    : 'This customer has no email address on file'
                }
                onClick={() => sendEmail.mutate()}
              >
                Email again
              </Button>
            </>
          )
        }
      />

      {emailNote && (
        <div className="mx-auto w-full max-w-[1860px] px-[30px] pb-4 text-[13px] text-ink-2">
          <Callout tone="info" icon="mail">
            {emailNote}
          </Callout>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-[30px]">
        <div className="mx-auto grid max-w-[1860px] grid-cols-1 items-start gap-5 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[minmax(265px,0.9fr)_minmax(560px,2.6fr)_minmax(280px,1fr)]">
          {/* ---------------------------------------------------- left rail */}
          <div className="space-y-5">
            <ContactCard
              title="Bill to"
              name={invoice.customer?.name ?? 'Customer'}
              role="Residential customer"
              phone={invoice.customer?.phone}
              email={invoice.customer?.email}
              property={invoice.property}
              customerId={invoice.customerId}
              jobId={invoice.job?.id}
              footLink={{ to: `/customers/${invoice.customerId}`, label: 'View customer profile' }}
            />

            <Card
              title="Terms"
              action={<Icon name="calendar" size={17} className="text-ink-4" />}
              bodyClassName="px-[21px] pt-1 pb-[21px]"
            >
              <dl className="text-[14px]">
                <Row label="Issued" value={invoice.issuedAt ? formatDate(invoice.issuedAt) : '—'} />
                <Row label="Due" value={invoice.dueAt ? formatDate(invoice.dueAt) : '—'} />
                <Row label="Tax rate" value={`${(invoice.taxRateBps / 100).toFixed(2)}%`} />
              </dl>
            </Card>

            <PaymentRequestPanel
              invoiceId={invoice.id}
              balanceCents={balance}
              customerPhone={invoice.customer?.phone ?? null}
            />
          </div>

          {/* ------------------------------------------------------- middle */}
          <div className="space-y-5">
            <FlowBar
              steps={flowSteps}
              busy={send.isPending || sendEmail.isPending}
              title="Invoice progress"
              status={
                isVoid
                  ? 'Void'
                  : balance > 0
                    ? 'Awaiting final payment'
                    : 'Settled in full'
              }
            />

            <Card
              title="Payments"
              icon="cost"
              divided
              bodyClassName="p-0"
              action={
                balance > 0 &&
                !isVoid && (
                  <Button variant="primary" size="sm" icon="plus" onClick={() => setTaking(true)}>
                    Record payment
                  </Button>
                )
              }
              band={
                <SummaryBand
                  tone={balance > 0 ? 'owed' : 'good'}
                  icon={balance > 0 ? undefined : 'check'}
                  label={balance < 0 ? 'Credit' : balance > 0 ? 'Balance due' : 'Paid in full'}
                  caption={
                    balance > 0 && invoice.dueAt ? `Due ${formatDate(invoice.dueAt)}` : undefined
                  }
                  amount={formatCents(Math.abs(balance))}
                />
              }
            >
              {payments.length === 0 ? (
                <p className="px-[25px] py-6 text-[13px] text-ink-4">Nothing received yet.</p>
              ) : (
                <ul>
                  {payments.map((payment) => (
                    <li
                      key={payment.id}
                      className={cx(
                        'flex items-center gap-3 border-b border-line px-[25px] py-[23px] last:border-b-0',
                        payment.voidedAt && 'opacity-55',
                      )}
                    >
                      <span
                        className={cx(
                          'grid size-9 shrink-0 place-items-center rounded-[9px] border',
                          payment.voidedAt
                            ? 'border-line-2 bg-raised text-ink-4'
                            : 'border-good/20 bg-good-soft text-good',
                        )}
                        aria-hidden
                      >
                        <Icon name={payment.voidedAt ? 'close' : 'check'} size={16} />
                      </span>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span
                            className={cx(
                              'text-[16px] font-semibold tnum',
                              payment.voidedAt ? 'line-through text-ink-3' : 'text-good',
                            )}
                          >
                            {formatCents(payment.amountCents)}
                          </span>
                          <span className="text-[12px] text-ink-3">
                            {PAYMENT_METHOD_LABELS[payment.method]}
                          </span>
                        </div>
                        <div className="mt-1 text-[12px] text-ink-3">
                          {payment.reference && (
                            <>
                              {payment.reference}
                              <br />
                            </>
                          )}
                          {formatDateTime(payment.createdAt)}
                          {payment.receivedByName && ` · ${payment.receivedByName}`}
                        </div>
                      </div>

                      {payment.voidedAt ? (
                        <span className="text-[12px] text-ink-4">Voided</span>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={voidPayment.isPending && voidPayment.variables === payment.id}
                          onClick={() => voidPayment.mutate(payment.id)}
                        >
                          Void
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card
              title="Line items"
              icon="file"
              count={invoice.lineItems?.length ?? 0}
              divided
              bodyClassName="p-0"
              action={
                !locked &&
                !editing && (
                  <button
                    type="button"
                    onClick={startEditing}
                    className="text-[13px] font-semibold text-accent hover:underline"
                  >
                    Edit items
                  </button>
                )
              }
              band={
                <SummaryBand
                  label="Invoice total"
                  caption="Includes applicable tax"
                  amount={formatCents(totals?.totalCents ?? 0)}
                />
              }
            >
              <LineItemEditor
                value={
                  editing
                    ? draft
                    : (invoice.lineItems ?? []).map((li) => ({
                        priceBookItemId: li.priceBookItemId,
                        name: li.name,
                        description: li.description,
                        kind: li.kind,
                        quantity: li.quantity,
                        unitPriceCents: li.unitPriceCents,
                        taxable: li.taxable,
                        position: li.position,
                      }))
                }
                onChange={setDraft}
                taxRateBps={invoice.taxRateBps}
                readOnly={!editing}
                /*
                 * Deliberately given neither the paid amount nor the grand
                 * total: the band below the card carries the total and the
                 * card above carries the balance. Printing either twice
                 * invites the two copies to disagree.
                 */
                hideTotal
                discount={{
                  type: editing ? discount.type : invoice.discountType,
                  valueCents: editing ? discount.valueCents : invoice.discountValueCents,
                  valueBps: editing ? discount.valueBps : invoice.discountValueBps,
                  onChange: setDiscount,
                }}
              />
            </Card>
          </div>

          {/* --------------------------------------------------- right rail */}
          <div className="space-y-5">
            <ActivityFeed entries={feedEntries} />
            {invoice.job && <CommunicationLog jobId={invoice.job.id} title="Calls & texts" />}
          </div>
        </div>

        {can('admin') && (
          <DeleteRecord
            one="invoice"
            many="invoices"
            warning="Payment requests attached to it go too. An invoice with payments that are not voided cannot be deleted — void them, or void the invoice instead."
            onConfirm={() => api.invoices.bulkRemove([invoice.id])}
            onDeleted={() => {
              void qc.invalidateQueries({ queryKey: ['invoices'] });
              navigate('/invoices');
            }}
          />
        )}

        <PageFooter label={`Invoice #${invoice.number}`} />
      </div>

      <RecordPaymentModal
        open={taking}
        onClose={() => setTaking(false)}
        invoiceId={invoice.id}
        balanceCents={balance}
        onSaved={() => {
          setTaking(false);
          refresh();
          setToast({ message: 'Payment recorded', tone: 'success' });
        }}
      />

      {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="my-[13px] flex items-center justify-between gap-3 first:mt-0 last:mb-0">
      <dt className="text-ink-3">{label}</dt>
      <dd className="tnum">{value}</dd>
    </div>
  );
}

function RecordPaymentModal({
  open,
  onClose,
  invoiceId,
  balanceCents,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  balanceCents: number;
  onSaved: () => void;
}) {
  const [amount, setAmount] = useState(() => (balanceCents / 100).toFixed(2));
  const [method, setMethod] = useState<PaymentMethod>('card');
  const [reference, setReference] = useState('');
  const [memo, setMemo] = useState('');
  const [allowOver, setAllowOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cents = parseCents(amount) ?? 0;
  const isOver = cents > Math.max(balanceCents, 0);

  const save = useMutation({
    mutationFn: () =>
      api.invoices.addPayment(invoiceId, {
        amountCents: cents,
        method,
        reference: reference.trim() || null,
        memo: memo.trim() || null,
        allowOverpayment: allowOver,
      }),
    onSuccess: onSaved,
    onError: (e) => {
      const err = e as { message?: string; details?: { code?: string } };
      setError(err.message ?? 'Could not record the payment');
      if (err.details?.code === 'overpayment') setAllowOver(false);
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Record a payment"
      description="Cash, cheque, or a card run through your own terminal."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={cents <= 0}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Record {formatCents(cents)}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount" required>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              className="tnum"
            />
          </Field>
          <Field label="Method">
            <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
              {(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="flex gap-2 text-[12.5px]">
          <button
            className="rounded-md border border-line-2 px-2 py-1 hover:bg-canvas"
            onClick={() => setAmount((balanceCents / 100).toFixed(2))}
          >
            Full balance ({formatCents(balanceCents)})
          </button>
          <button
            className="rounded-md border border-line-2 px-2 py-1 hover:bg-canvas"
            onClick={() => setAmount((balanceCents / 200).toFixed(2))}
          >
            Half
          </button>
        </div>

        <Field label="Reference" hint="cheque number, card last four">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} />
        </Field>
        <Field label="Memo" hint="optional">
          <Textarea rows={2} value={memo} onChange={(e) => setMemo(e.target.value)} />
        </Field>

        {isOver && (
          <label className="flex items-start gap-2 rounded-lg bg-warn-soft px-3 py-2 text-[13px] text-warn">
            <input
              type="checkbox"
              checked={allowOver}
              onChange={(e) => setAllowOver(e.target.checked)}
              className="mt-0.5 size-4 accent-current"
            />
            <span>
              That is more than the {formatCents(balanceCents)} owed. Tick to record the
              overpayment deliberately.
            </span>
          </label>
        )}

        {error && (
          <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>
        )}
      </div>
    </Modal>
  );
}
